import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, TrendingDown, AlertTriangle, Clock, Users, 
  RefreshCw, Trash2, ExternalLink, Loader2, Calendar,
  ArrowLeft, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const LegacyUsageMonitor = ({ onBackToHome }) => {
  const { t } = useTranslation();
  const { isAuth, isCheckingAuth, handleLogin, handleLogout, getAuthHeader } = useAdminAuth();
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [endpointDetails, setEndpointDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState(7);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeader();
      const baseUrl = API_URL || '';
      
      // Fetch summary (use legacy /api/ since this is an admin endpoint in api_router)
      const summaryRes = await fetch(
        `${baseUrl}/api/admin/legacy-usage/summary?days=${days}`,
        { headers }
      );
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
      
      // Fetch timeline
      const timelineRes = await fetch(
        `${baseUrl}/api/admin/legacy-usage/timeline?days=${days}`,
        { headers }
      );
      if (timelineRes.ok) {
        setTimeline((await timelineRes.json()).timeline || []);
      }
    } catch (error) {
      logger.error('Error fetching legacy usage data:', error);
      toast.error('Failed to load usage data');
    } finally {
      setIsLoading(false);
    }
  }, [days, getAuthHeader]);

  const fetchEndpointDetails = async (endpoint) => {
    try {
      const headers = getAuthHeader();
      const cleanPath = endpoint.replace('/api/', '');
      const baseUrl = API_URL || '';
      const res = await fetch(
        `${baseUrl}/api/admin/legacy-usage/details/${cleanPath}?days=${days}`,
        { headers }
      );
      if (res.ok) {
        setEndpointDetails(await res.json());
        setSelectedEndpoint(endpoint);
      }
    } catch (error) {
      toast.error('Failed to load endpoint details');
    }
  };

  const handleClearOldData = async () => {
    if (!window.confirm('Clear usage data older than 30 days?')) return;
    
    try {
      const headers = getAuthHeader();
      const baseUrl = API_URL || '';
      const res = await fetch(
        `${baseUrl}/api/admin/legacy-usage/clear?older_than_days=30`,
        { method: 'DELETE', headers }
      );
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to clear data');
    }
  };

  useEffect(() => {
    if (isAuth) {
      fetchData();
    }
  }, [isAuth, fetchData]);

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuth) {
    return <AdminLogin onLogin={handleLogin} title="Legacy API Monitor" />;
  }

  const maxCalls = Math.max(...timeline.map(t => t.calls), 1);

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader
        title="Legacy API Usage Monitor"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
      />

      <div className="container mx-auto px-6 py-8">
        {/* Period Selector */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Calendar className="w-5 h-5 text-gray-500" />
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-4 py-2 border rounded-lg bg-white"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleClearOldData} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Old Data
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-orange-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Total Calls</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {summary?.total_legacy_calls?.toLocaleString() || 0}
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <ExternalLink className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Unique Endpoints</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {summary?.unique_endpoints_called || 0}
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Status</span>
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {summary?.total_legacy_calls > 0 ? (
                    <span className="text-orange-600">Migration Needed</span>
                  ) : (
                    <span className="text-green-600">All Clear</span>
                  )}
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-red-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Sunset Date</span>
                </div>
                <div className="text-lg font-bold text-red-600">
                  Jan 1, 2027
                </div>
              </div>
            </div>

            {/* Timeline Chart */}
            {timeline.length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                <h3 className="text-lg font-semibold mb-4">Usage Timeline</h3>
                <div className="h-40 flex items-end gap-1">
                  {timeline.map((day, idx) => (
                    <div key={`bar-${idx}`} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-orange-400 rounded-t transition-all hover:bg-orange-500"
                        style={{ height: `${(day.calls / maxCalls) * 100}%`, minHeight: day.calls > 0 ? '4px' : '0' }}
                        title={`${day.date}: ${day.calls} calls`}
                      />
                      <span className="text-xs text-gray-500 mt-1 rotate-45 origin-left">
                        {day.date.slice(5)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Endpoints Table */}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="font-semibold">Legacy Endpoints Being Used</h3>
                </div>
                {summary?.endpoints?.length > 0 ? (
                  <div className="divide-y max-h-96 overflow-y-auto">
                    {summary.endpoints.map((ep, idx) => (
                      <div 
                        key={idx}
                        className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                        onClick={() => fetchEndpointDetails(ep.endpoint)}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                              ep.method === 'GET' ? 'bg-green-100 text-green-700' :
                              ep.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                              ep.method === 'PUT' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {ep.method}
                            </span>
                            <code className="text-sm">{ep.endpoint}</code>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {ep.total_calls} calls • {ep.unique_clients} clients • {Math.round(ep.success_rate)}% success
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <TrendingDown className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No legacy endpoint usage detected!</p>
                    <p className="text-sm">All integrations are using v1 endpoints.</p>
                  </div>
                )}
              </div>

              {/* Endpoint Details */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="font-semibold">
                    {selectedEndpoint ? 'Endpoint Details' : 'Select an Endpoint'}
                  </h3>
                </div>
                {endpointDetails ? (
                  <div className="p-4">
                    <div className="mb-4">
                      <div className="text-sm text-gray-500">Legacy Endpoint</div>
                      <code className="text-red-600">{endpointDetails.endpoint}</code>
                    </div>
                    <div className="mb-4">
                      <div className="text-sm text-gray-500">V1 Equivalent</div>
                      <code className="text-green-600">{endpointDetails.v1_equivalent}</code>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-2xl font-bold">{endpointDetails.total_calls}</div>
                        <div className="text-xs text-gray-500">Total Calls</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-2xl font-bold">{endpointDetails.unique_clients}</div>
                        <div className="text-xs text-gray-500">Unique Clients</div>
                      </div>
                    </div>
                    {endpointDetails.referers?.length > 0 && (
                      <div className="mb-4">
                        <div className="text-sm font-medium mb-2">Top Referrers</div>
                        <div className="space-y-1">
                          {endpointDetails.referers.slice(0, 5).map((ref, idx) => (
                            <div key={`recent-${idx}`} className="text-xs bg-gray-50 p-2 rounded truncate">
                              {ref}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium mb-2">Recent Calls</div>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {endpointDetails.recent_calls?.slice(0, 10).map((call, idx) => (
                          <div key={`agent-${idx}`} className="text-xs bg-gray-50 p-2 rounded flex justify-between">
                            <span>{call.timestamp?.slice(0, 19)}</span>
                            <span className={call.status_code < 300 ? 'text-green-600' : 'text-red-600'}>
                              {call.status_code}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-2" />
                    <p>Click an endpoint to see details</p>
                  </div>
                )}
              </div>
            </div>

            {/* Migration Guide */}
            <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">Migration Guide</h3>
              <p className="text-blue-800 text-sm mb-4">
                All legacy <code className="bg-blue-100 px-1 rounded">/api/*</code> endpoints should be migrated to 
                <code className="bg-green-100 px-1 rounded">/api/v1/*</code> before January 1, 2027.
              </p>
              <div className="text-sm text-blue-700">
                <strong>Example:</strong> Change <code>/api/services/admin</code> → <code>/api/v1/services/admin</code>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LegacyUsageMonitor;
